import { Component, DestroyRef, input, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Nationality } from '@app/core/stingray-api/models';
import { finalize, map } from 'rxjs/operators';
import { requireMatch } from '@app/validators/autocomplete-validator';
import { NationalityAutoCompleteService } from './nationality-autocomplete.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';

@Component({
    selector: 'unite-nationality-autocomplete',
    templateUrl: './nationality-autocomplete.component.html',
    styleUrls: ['./nationality-autocomplete.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatFormField, NgClass, MatLabel, MatProgressSpinner, MatSuffix, MatInput, ReactiveFormsModule, MatAutocompleteTrigger, MatAutocomplete, MatOption, TranslateModule]
})
export class NationalityAutocompleteComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  control = input<FormControl>();
  focus = input(false);
  label = input<string>();
  appearance = input<string>('outline');
  class = input<string>('');
  showPlaceholder = input(false);

  nationalities: Nationality[] = [];
  nationalitiesFiltered: Nationality[];

  isLoadingNationalities = false;

  constructor(
    private nationalityAutoCompleteService: NationalityAutoCompleteService) { }

  ngOnInit() {
    if (!this.control().hasValidator(Validators.required)) {
      this.control().setValidators([requireMatch]);
    } else {
      this.control().setValidators([Validators.required, requireMatch]);
    }

    this.isLoadingNationalities = true;
    this.nationalityAutoCompleteService.getNationalities().pipe(
      finalize(() => {
        this.isLoadingNationalities = false;
      })
    ).subscribe((customers: Nationality[]) => {
      this.nationalities = customers;
      this.nationalitiesFiltered = customers;
    });

    this.control().valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Nationality) => {
        if (this.isNationality(value)) {
          return this.filterNationality(value.name);
        } else {
          return this.filterNationality(value || '');
        }
      })
    ).subscribe((nationalities: Nationality[]) => {
      this.nationalitiesFiltered = nationalities;
    });
  }

  showValidationError(): boolean {
    return this.control().touched && this.control().valid === false;
  }

  displayWithNationality(nationality: Nationality): string {
    if (nationality != null) {
      return nationality.name;
    } else {
      return '';
    }
  }

  filterNationality(value: string): Nationality[] {
    const filterValue = value.toLowerCase();
    return this.nationalities.filter((nationality: Nationality) => {
      return this.displayWithNationality(nationality).toLowerCase().includes(filterValue) ||
      nationality.code.toLowerCase().includes(filterValue);
    });
  }

  isNationality(value: any | Nationality): value is Nationality {
    return value != null && (<Nationality>value).code !== undefined;
  }
}
